import React, { useCallback, useState } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { usePurchasePaidCreditsMutation } from '../../store/plan/plan'
import { type PaidCreditTier } from '../../models/Plan'
import { CROSS_CLIENT_QUERY_WALLET } from '../../models/Wallet'
import { Modal } from '../../components/Modal/Modal'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { formatCredits, formatCurrencyFromCents } from './walletFormat'
import { lowestMinQuantity, nextTierAbove, tierForQuantity } from './creditTiers'

export type PurchaseCreditsModalSource = 'walletTab' | 'lockScreen'

interface PurchaseCreditsModalProps {
  show: boolean
  onHide: () => void
  tiers: PaidCreditTier[] | null
  paymentType: 'autoCharge' | 'emailInvoice' | null
  hasPaymentMethod: boolean
  source: PurchaseCreditsModalSource
  onPurchaseSuccess?: () => void
}

export const PurchaseCreditsModal: React.FC<PurchaseCreditsModalProps> = ({
  show,
  onHide,
  tiers,
  paymentType,
  hasPaymentMethod,
  source,
  onPurchaseSuccess,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { tracked } = useTracking()
  const [quantity, setQuantity] = useState(1000)
  const [purchasePaidCredits, { isLoading }] = usePurchasePaidCreditsMutation()

  const handleQuantityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10)
    if (!isNaN(value) && value > 0) {
      setQuantity(value)
    }
  }, [])

  const _handlePurchase = useCallback(async () => {
    try {
      await purchasePaidCredits({ accountId, quantity, walletType: CROSS_CLIENT_QUERY_WALLET }).unwrap()
      onPurchaseSuccess?.()
      onHide()
    } catch (e) {
      console.error(e)
    }
  }, [accountId, quantity, purchasePaidCredits, onHide, onPurchaseSuccess])

  const handlePurchase = tracked(_handlePurchase, { eventName: TrackEvent.WALLET_PURCHASE_CONFIRMED }, { eventDetailsExtractor: () => ({ quantity, source }) })

  const pricingUnavailable = !tiers || tiers.length === 0
  const applicableTier = tiers ? tierForQuantity(tiers, quantity) : null
  const nextTier = tiers ? nextTierAbove(tiers, quantity) : null
  const minimumQuantity = tiers ? lowestMinQuantity(tiers) : null
  const unmetMinimum = minimumQuantity !== null && quantity < minimumQuantity ? minimumQuantity : null

  const totalPrice = applicableTier ? formatCurrencyFromCents(applicableTier.price.amount * quantity, applicableTier.price.currency) : null
  const perCreditPrice = applicableTier ? formatCurrencyFromCents(applicableTier.price.amount, applicableTier.price.currency) : null

  const missingPaymentMethod = paymentType === 'autoCharge' && !hasPaymentMethod
  const purchaseDisabled = quantity < 1 || pricingUnavailable || unmetMinimum !== null || missingPaymentMethod

  const body = (
    <Container className='p-0'>
      <Row className='mb-3'>
        <Col>
          <p className='text-muted mb-0'>
            Confirm to purchase <strong>{formatCredits(quantity)}</strong> credits
            {totalPrice ? (
              <>
                {' '}
                for <strong>{totalPrice}</strong>
              </>
            ) : null}
            .
          </p>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group controlId='creditQuantity'>
            <Form.Label>Credits</Form.Label>
            <Form.Control type='number' min={1} value={quantity} onChange={handleQuantityChange} />
          </Form.Group>
        </Col>
      </Row>
      {pricingUnavailable ? (
        <Row className='mt-3'>
          <Col>
            <div className='zocks-bg-color-warning p-3 zocks-border-0.5 rounded small'>
              Pricing is unavailable for this plan. Contact support to purchase credits.
            </div>
          </Col>
        </Row>
      ) : unmetMinimum !== null ? (
        <Row className='mt-3'>
          <Col>
            <div className='zocks-bg-color-warning p-3 zocks-border-0.5 rounded small'>
              The minimum purchase is <strong>{formatCredits(unmetMinimum)}</strong> credits.
            </div>
          </Col>
        </Row>
      ) : applicableTier ? (
        <Row className='mt-3'>
          <Col>
            <div className='p-3 zocks-border-0.5 rounded'>
              <div className='d-flex justify-content-between'>
                <span className='text-muted'>Price per credit</span>
                <span>{perCreditPrice}</span>
              </div>
              <div className='d-flex justify-content-between mt-1'>
                <span className='fw-semibold'>Total</span>
                <span className='fw-semibold'>{totalPrice}</span>
              </div>
              {nextTier ? (
                <div className='small text-muted mt-2'>
                  Buy {formatCredits(nextTier.minQuantity)}+ credits for {formatCurrencyFromCents(nextTier.price.amount, nextTier.price.currency)} per credit.
                </div>
              ) : null}
            </div>
          </Col>
        </Row>
      ) : null}
      {missingPaymentMethod ? (
        <Row className='mt-3'>
          <Col>
            <div className='zocks-bg-color-warning p-3 zocks-border-0.5 rounded small'>Add a payment method on the Billing tab to purchase credits.</div>
          </Col>
        </Row>
      ) : null}
    </Container>
  )

  return (
    <Modal
      show={show}
      onHide={onHide}
      header='Purchase credits'
      body={body}
      showFooterActions
      centered
      onFooterPrimaryAction={handlePurchase}
      footerPrimaryActionLoading={isLoading}
      footerPrimaryActionTitle='Confirm purchase'
      footerPrimaryActionDisabled={purchaseDisabled}
    />
  )
}
